import { useSelectCurrentUserInfo } from '../data';

type UserPermission = {
  id: string;
  permissionCode: string;
  nameTranslationKey: string;
  descriptionTranslationKey?: string | null;
  organizationId?: string | null;
};

type CurrentUserInfoWithPermissions = {
  permissions?: UserPermission[];
};

export const useCurrentUser = () => {
  const currentUser = useSelectCurrentUserInfo();

  const userWithPermissions = currentUser as CurrentUserInfoWithPermissions;
  const permissions = userWithPermissions?.permissions ?? [];

  const hasPermission = (permissionCode: string, organizationId?: string | null): boolean => {
    if (organizationId == null) {
      return permissions.some(p => p.permissionCode === permissionCode && p.organizationId == null);
    }

    return permissions.some(
      p =>
        p.permissionCode === permissionCode &&
        (p.organizationId === organizationId || p.organizationId == null)
    );
  };

  const hasAnyPermission = (...permissionCodes: string[]): boolean =>
    permissionCodes.some(code => hasPermission(code));

  const hasAnyPermissionInOrganization = (
    organizationId: string,
    ...permissionCodes: string[]
  ): boolean => permissionCodes.some(code => hasPermission(code, organizationId));

  const hasAllPermissions = (...permissionCodes: string[]): boolean =>
    permissionCodes.every(code => hasPermission(code));

  const hasAllPermissionsInOrganization = (
    organizationId: string,
    ...permissionCodes: string[]
  ): boolean => permissionCodes.every(code => hasPermission(code, organizationId));

  const getPermissionsInOrganization = (organizationId: string): UserPermission[] =>
    permissions.filter(p => p.organizationId === organizationId || p.organizationId == null);

  const getGlobalPermissions = (): UserPermission[] =>
    permissions.filter(p => p.organizationId == null);

  const getOrganizationIds = (): string[] => {
    const orgIds = permissions.map(p => p.organizationId).filter((id): id is string => id != null);

    return Array.from(new Set(orgIds));
  };

  const getOrganizationsWithPermission = (permissionCode: string): string[] => {
    const orgIds = permissions
      .filter(p => p.permissionCode === permissionCode && p.organizationId != null)
      .map(p => p.organizationId)
      .filter((id): id is string => id != null);

    return Array.from(new Set(orgIds));
  };

  const hasPermissionInAnyOrganization = (permissionCode: string): boolean =>
    permissions.some(p => p.permissionCode === permissionCode && p.organizationId != null);

  const hasGlobalPermission = (permissionCode: string): boolean =>
    permissions.some(p => p.permissionCode === permissionCode && p.organizationId == null);

  return {
    currentUser,
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAnyPermissionInOrganization,
    hasAllPermissions,
    hasAllPermissionsInOrganization,
    getPermissionsInOrganization,
    getGlobalPermissions,
    getOrganizationIds,
    getOrganizationsWithPermission,
    hasPermissionInAnyOrganization,
    hasGlobalPermission
  };
};
